import { useState, useEffect } from "react";
import { Plus, Trash2, Send, Loader2, Building2, Users, Briefcase, ShieldCheck, Search, AlertTriangle, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const BOARD_ROLES = ["Styreleder", "Nestleder", "Styremedlem", "Varamedlem", "Daglig leder"];

const emptyOwner = () => ({ kind: "person", name: "", email: "", national_id: "", org_number: "", shares: "", contact_name: "", contact_email: "", contact_national_id: "" });
const emptyBoard = () => ({ name: "", email: "", national_id: "", role: "Styremedlem" });

export function ManualCompanyForm({ onDone, initialOrg = "", mode = "manual", proffRole = "" }) {
  const isProffConfirm = mode === "proff-confirm";
  const [c, setC] = useState({
    name: "", org_number: initialOrg || "", address: "", postal_code: "", city: "", industry: "",
    share_capital: "", total_shares: "", share_class_name: "Ordinære aksjer", my_role: proffRole || "Styreleder", note: "",
  });
  const [owners, setOwners] = useState([emptyOwner()]);
  const [board, setBoard] = useState([emptyBoard()]);
  const [sending, setSending] = useState(false);
  const [proffLoading, setProffLoading] = useState(false);
  const [existsInfo, setExistsInfo] = useState(null); // {message, company_id, name, org_number}
  const [requesting, setRequesting] = useState(false);
  const [contactOwnerIdx, setContactOwnerIdx] = useState(-1); // hvilken bedrift-eier kontaktperson-popupen gjelder

  const setField = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const updRow = (list, setList, i, k, v) => setList(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const fetchProff = async (orgOverride) => {
    const org = String(orgOverride ?? c.org_number ?? "").replace(/\D/g, "");
    if (org.length !== 9) {
      toast.error("Skriv inn et gyldig organisasjonsnummer (9 siffer)");
      return;
    }
    setProffLoading(true);
    try {
      const { data } = await api.get(`/proff/company/${org}`);
      setC((p) => ({
        ...p,
        name: data.name || p.name,
        org_number: data.org_number || p.org_number,
        address: data.address || p.address,
        postal_code: data.postal_code || p.postal_code,
        city: data.city || p.city,
        industry: data.industry || p.industry,
        share_capital: data.share_capital || p.share_capital,
        total_shares: (data.shareholders || []).reduce((s, x) => s + (x.shares || 0), 0) || p.total_shares,
      }));
      if (data.board?.length) {
        setBoard(data.board.map((b) => ({ name: b.name, email: "", national_id: "", role: b.role })));
      }
      const parts = ["selskapsinfo"];
      if (data.shareholders?.length) {
        setOwners(
          data.shareholders.map((s) => ({
            kind: s.kind, name: s.name, email: "", national_id: "", org_number: s.org_number || "", shares: s.shares || "",
            contact_name: "", contact_email: "", contact_national_id: "",
          }))
        );
        parts.push(`${data.shareholders.length} aksjonær(er)`);
      }
      if (data.board?.length) parts.push(`${data.board.length} styremedlem(mer)`);
      toast.success(`Hentet ${parts.join(", ")} fra Proff`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProffLoading(false);
    }
  };

  useEffect(() => {
    const org = String(initialOrg || "").replace(/\D/g, "");
    if (org.length === 9) fetchProff(org);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!c.name.trim()) {
      toast.error("Skriv inn selskapsnavn");
      return;
    }
    const missingContactIdx = owners.findIndex(
      (o) => o.name.trim() && o.kind === "company" &&
        (!(o.contact_name || "").trim() || !(o.contact_email || "").trim())
    );
    if (missingContactIdx !== -1) {
      toast.error("Hver bedrift-eier må ha en kontaktperson med navn og e-post.");
      setContactOwnerIdx(missingContactIdx);
      return;
    }
    const payload = {
      ...c,
      share_capital: Number(c.share_capital) || 0,
      total_shares: Number(c.total_shares) || 0,
      owners: owners
        .filter((o) => o.name.trim())
        .map((o) => ({
          kind: o.kind,
          name: o.name.trim(),
          email: o.kind === "company" ? "" : o.email.trim(),
          national_id: o.kind === "company" ? "" : o.national_id.trim(),
          org_number: o.kind === "company" ? o.org_number.trim() : "",
          shares: Number(o.shares) || 0,
          contact_name: o.kind === "company" ? (o.contact_name || "").trim() : "",
          contact_email: o.kind === "company" ? (o.contact_email || "").trim() : "",
          contact_national_id: o.kind === "company" ? (o.contact_national_id || "").replace(/\D/g, "") : "",
        })),
      board: board
        .filter((b) => b.name.trim())
        .map((b) => ({ name: b.name.trim(), email: b.email.trim(), national_id: b.national_id.trim(), role: b.role })),
    };
    setSending(true);
    try {
      if (isProffConfirm) {
        const { data } = await api.post("/companies/add-from-proff/confirm", payload);
        toast.success(`${data.name || payload.name} lagt til`);
      } else {
        await api.post("/companies/register-manual", payload);
        toast.success("Selskap sendt til admin for godkjenning");
      }
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "company_exists") {
        setExistsInfo(detail);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setSending(false);
    }
  };

  // Selskapet finnes allerede → be om tilgang i stedet for å opprette duplikat
  const requestAccess = async () => {
    setRequesting(true);
    try {
      await api.post("/companies/request-access", {
        name: existsInfo?.name || c.name.trim(),
        org_number: existsInfo?.org_number || c.org_number || "",
        role: c.my_role || "Styremedlem",
        note: "Ba om tilgang fordi selskapet allerede finnes i Styrerom.",
      });
      toast.success("Forespørsel om tilgang sendt til admin");
      setExistsInfo(null);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1" data-testid="manual-company-form">
      {/* Selskapsinfo */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Building2 className="h-4 w-4 text-primary" /> Selskapsinfo</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Selskapsnavn *</Label>
            <Input data-testid="manual-name-input" className="mt-1.5" value={c.name} onChange={(e) => setField("name", e.target.value)} placeholder="Eksempel AS" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Org.nr</Label>
            <div className="mt-1.5 flex gap-2">
              <Input data-testid="manual-org-input" value={c.org_number} onChange={(e) => setField("org_number", e.target.value)} placeholder="9 siffer" inputMode="numeric" />
              <Button type="button" variant="outline" className="shrink-0 gap-1.5" data-testid="proff-lookup-btn" onClick={() => fetchProff()} disabled={proffLoading}>
                {proffLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Hent fra Proff
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Skriv org.nr og hent selskapsinfo og aksjonærer automatisk fra Proff.</p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Bransje</Label>
            <Input data-testid="manual-industry-input" className="mt-1.5" value={c.industry} onChange={(e) => setField("industry", e.target.value)} placeholder="f.eks. Teknologi" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Adresse</Label>
            <Input data-testid="manual-address-input" className="mt-1.5" value={c.address} onChange={(e) => setField("address", e.target.value)} placeholder="Gate og nummer" />
          </div>
          <div>
            <Label className="text-xs">Postnr</Label>
            <Input data-testid="manual-postal-input" className="mt-1.5" value={c.postal_code} onChange={(e) => setField("postal_code", e.target.value)} placeholder="0000" inputMode="numeric" />
          </div>
          <div>
            <Label className="text-xs">Poststed</Label>
            <Input data-testid="manual-city-input" className="mt-1.5" value={c.city} onChange={(e) => setField("city", e.target.value)} placeholder="By" />
          </div>
          <div>
            <Label className="text-xs">Aksjekapital (kr)</Label>
            <Input data-testid="manual-capital-input" type="number" className="mt-1.5" value={c.share_capital} onChange={(e) => setField("share_capital", e.target.value)} placeholder="0" inputMode="numeric" />
          </div>
          <div>
            <Label className="text-xs">Antall aksjer</Label>
            <Input data-testid="manual-shares-input" type="number" className="mt-1.5" value={c.total_shares} onChange={(e) => setField("total_shares", e.target.value)} placeholder="0" inputMode="numeric" />
          </div>
          <div>
            <Label className="text-xs">Aksjeklasse</Label>
            <Input data-testid="manual-class-input" className="mt-1.5" value={c.share_class_name} onChange={(e) => setField("share_class_name", e.target.value)} placeholder="Ordinære aksjer" />
          </div>
          <div>
            <Label className="text-xs">Din rolle</Label>
            {isProffConfirm ? (
              <div className="mt-1.5 flex h-9 items-center rounded-md border border-border/60 bg-muted/40 px-3 text-sm" data-testid="proff-myrole-fixed">
                {c.my_role} <span className="ml-1.5 text-xs text-muted-foreground">(fra Proff)</span>
              </div>
            ) : (
              <Select value={c.my_role} onValueChange={(v) => setField("my_role", v)}>
                <SelectTrigger data-testid="manual-myrole-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{BOARD_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      </section>

      {/* Eiere */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" /> Eiere (aksjonærer)</div>
          <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" data-testid="add-owner-btn" onClick={() => setOwners([...owners, emptyOwner()])}>
            <Plus className="h-3.5 w-3.5" /> Legg til eier
          </Button>
        </div>
        {owners.map((o, i) => (
          <div key={i} data-testid={`owner-row-${i}`} className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-2.5">
            <div className="flex items-center gap-2">
              <Select value={o.kind} onValueChange={(v) => updRow(owners, setOwners, i, "kind", v)}>
                <SelectTrigger data-testid={`owner-kind-${i}`} className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="company">Bedrift</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">{o.kind === "company" ? "Aksjeeier som er et selskap" : "Aksjeeier som er en person"}</span>
              <button type="button" data-testid={`remove-owner-${i}`} className="ml-auto grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:text-destructive disabled:opacity-40" onClick={() => setOwners(owners.filter((_, idx) => idx !== i))} disabled={owners.length === 1}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {o.kind === "company" ? (
              <>
                <div className="grid grid-cols-12 gap-2">
                  <Input data-testid={`owner-name-${i}`} className="col-span-5 h-9" placeholder="Firmanavn" value={o.name} onChange={(e) => updRow(owners, setOwners, i, "name", e.target.value)} />
                  <Input data-testid={`owner-orgnr-${i}`} className="col-span-4 h-9" placeholder="Org.nr" value={o.org_number} onChange={(e) => updRow(owners, setOwners, i, "org_number", e.target.value)} inputMode="numeric" />
                  <Input data-testid={`owner-shares-${i}`} type="number" className="col-span-3 h-9" placeholder="Aksjer" value={o.shares} onChange={(e) => updRow(owners, setOwners, i, "shares", e.target.value)} inputMode="numeric" />
                </div>
                {(() => {
                  const done = (o.contact_name || "").trim() && (o.contact_email || "").trim();
                  return (
                    <Button type="button" variant="outline" size="sm"
                      data-testid={`owner-contact-btn-${i}`}
                      className={`h-9 w-full justify-start gap-2 ${done ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-300" : "border-amber-500/50 text-amber-700 dark:text-amber-300"}`}
                      onClick={() => setContactOwnerIdx(i)}>
                      {done ? <Check className="h-4 w-4 shrink-0" /> : <UserPlus className="h-4 w-4 shrink-0" />}
                      <span className="truncate">{done ? `Kontaktperson: ${o.contact_name}` : "Legg til kontaktperson *"}</span>
                    </Button>
                  );
                })()}
              </>
            ) : (
              <div className="grid grid-cols-12 gap-2">
                <Input data-testid={`owner-name-${i}`} className="col-span-4 h-9" placeholder="Navn" value={o.name} onChange={(e) => updRow(owners, setOwners, i, "name", e.target.value)} />
                <Input data-testid={`owner-email-${i}`} className="col-span-3 h-9" placeholder="E-post" value={o.email} onChange={(e) => updRow(owners, setOwners, i, "email", e.target.value)} />
                <Input data-testid={`owner-nid-${i}`} className="col-span-3 h-9" placeholder="Fødselsnr" value={o.national_id} onChange={(e) => updRow(owners, setOwners, i, "national_id", e.target.value)} inputMode="numeric" />
                <Input data-testid={`owner-shares-${i}`} type="number" className="col-span-2 h-9" placeholder="Aksjer" value={o.shares} onChange={(e) => updRow(owners, setOwners, i, "shares", e.target.value)} inputMode="numeric" />
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Styre */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold"><Briefcase className="h-4 w-4 text-primary" /> Styret</div>
          <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" data-testid="add-board-btn" onClick={() => setBoard([...board, emptyBoard()])}>
            <Plus className="h-3.5 w-3.5" /> Legg til styremedlem
          </Button>
        </div>
        {board.map((b, i) => (
          <div key={i} data-testid={`board-row-${i}`} className="grid grid-cols-12 gap-2 rounded-lg border border-border/60 bg-muted/20 p-2.5">
            <Input data-testid={`board-name-${i}`} className="col-span-3 h-9" placeholder="Navn" value={b.name} onChange={(e) => updRow(board, setBoard, i, "name", e.target.value)} />
            <Input data-testid={`board-email-${i}`} className="col-span-3 h-9" placeholder="E-post" value={b.email} onChange={(e) => updRow(board, setBoard, i, "email", e.target.value)} />
            <Input data-testid={`board-nid-${i}`} className="col-span-2 h-9" placeholder="Fødselsnr" value={b.national_id} onChange={(e) => updRow(board, setBoard, i, "national_id", e.target.value)} inputMode="numeric" />
            <div className="col-span-3">
              <Select value={b.role} onValueChange={(v) => updRow(board, setBoard, i, "role", v)}>
                <SelectTrigger data-testid={`board-role-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{BOARD_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button type="button" data-testid={`remove-board-${i}`} className="col-span-1 grid place-items-center text-muted-foreground hover:text-destructive" onClick={() => setBoard(board.filter((_, idx) => idx !== i))} disabled={board.length === 1}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </section>

      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        {isProffConfirm
          ? "Informasjonen er hentet fra Proff. Rett opp ved behov – selskapet legges til direkte siden rollen din er bekreftet i Proff."
          : "En admin gjennomgår og godkjenner selskapet før det opprettes. Eiere og styremedlemmer med e-post får en invitasjon."}
      </div>

      <Button className="w-full gap-2" data-testid="manual-submit-btn" onClick={submit} disabled={sending}>
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {isProffConfirm ? "Legg til selskap" : "Send til godkjenning"}
      </Button>

      {/* Selskapet finnes allerede → be om tilgang */}
      <Dialog open={!!existsInfo} onOpenChange={(o) => { if (!o) setExistsInfo(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="company-exists-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Selskapet finnes allerede
            </DialogTitle>
            <DialogDescription data-testid="company-exists-message">
              {existsInfo?.message || `«${existsInfo?.name}» er allerede registrert i Styrerom.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            For å unngå dobbeltregistrering kan du be om tilgang til det eksisterende selskapet. En admin eller styreleder gir deg tilgang.
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="company-exists-cancel" onClick={() => setExistsInfo(null)} disabled={requesting}>
              Avbryt
            </Button>
            <Button className="gap-2" data-testid="company-exists-request-access" onClick={requestAccess} disabled={requesting}>
              {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Be om tilgang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OwnerContactDialog
        owner={contactOwnerIdx >= 0 ? owners[contactOwnerIdx] : null}
        onClose={() => setContactOwnerIdx(-1)}
        onSave={(contact) => {
          setOwners((prev) => prev.map((o, idx) => (idx === contactOwnerIdx ? { ...o, ...contact } : o)));
          setContactOwnerIdx(-1);
        }}
      />
    </div>
  );
}

const isValidContact = (c) =>
  (c.contact_name || "").trim() && (c.contact_email || "").trim();

// Popup: velg/legg inn kontaktperson for en bedrift-eier. Kandidater = styret til eier-bedriften
// (fra Styrerom hvis den finnes der, ellers fra Proff på eier-bedriftens org.nr).
function OwnerContactDialog({ owner, onClose, onSave }) {
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_national_id: "" });
  const [candidates, setCandidates] = useState([]);
  const [source, setSource] = useState(null); // {in_system, company_name}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!owner) return;
    setForm({
      contact_name: owner.contact_name || "",
      contact_email: owner.contact_email || "",
      contact_national_id: owner.contact_national_id || "",
    });
    setCandidates([]);
    setSource(null);
    const org = String(owner.org_number || "").replace(/\D/g, "");
    if (org.length !== 9) return;
    setLoading(true);
    api
      .get(`/companies/owner-contact-candidates/${org}`)
      .then(({ data }) => {
        setCandidates(data.candidates || []);
        setSource({ in_system: data.in_system, company_name: data.company_name });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [owner]);

  const pick = (cand) =>
    setForm({
      contact_name: cand.name || "",
      contact_email: cand.email || "",
      contact_national_id: cand.national_id || "",
    });

  const save = () => {
    if (!isValidContact(form)) {
      toast.error("Kontaktperson må ha navn og e-post.");
      return;
    }
    onSave({
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim(),
      contact_national_id: form.contact_national_id.replace(/\D/g, ""),
    });
  };

  return (
    <Dialog open={!!owner} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="owner-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Kontaktperson for {owner?.name || "bedriften"}
          </DialogTitle>
          <DialogDescription>
            Bedrifter som er eiere må ha en kontaktperson med navn og e-post. Personen representerer selskapet. Fødselsnummer trenger du ikke oppgi – systemet henter det når kontaktpersonen selv logger inn med BankID første gang.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="owner-contact-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Henter styret til eier-bedriften …
          </div>
        ) : candidates.length ? (
          <div className="space-y-1.5" data-testid="owner-contact-candidates">
            <p className="text-xs text-muted-foreground">
              {source?.in_system ? "Foreslått fra styret i Styrerom" : "Foreslått fra styret i Proff"}
              {source?.company_name ? ` · ${source.company_name}` : ""}. Velg en person eller fyll inn ny under.
            </p>
            {candidates.map((cand, i) => (
              <button
                key={i}
                type="button"
                data-testid={`owner-contact-candidate-${i}`}
                onClick={() => pick(cand)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-left text-sm hover:border-primary/50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{cand.name}</span>
                  {cand.role ? <span className="block text-xs text-muted-foreground">{cand.role}</span> : null}
                </span>
                {cand.national_id ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
              </button>
            ))}
          </div>
        ) : null}

        <div className="space-y-2">
          <div>
            <Label className="text-xs">Navn *</Label>
            <Input data-testid="owner-contact-name" className="mt-1.5" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} placeholder="Ola Nordmann" />
          </div>
          <div>
            <Label className="text-xs">E-post *</Label>
            <Input data-testid="owner-contact-email" className="mt-1.5" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} placeholder="ola@eksempel.no" />
          </div>
          <div>
            <Label className="text-xs">Fødselsnummer (valgfritt)</Label>
            <Input data-testid="owner-contact-nid" className="mt-1.5" value={form.contact_national_id} onChange={(e) => setForm((f) => ({ ...f, contact_national_id: e.target.value }))} placeholder="Hentes ved første BankID-innlogging" inputMode="numeric" />
            <p className="mt-1 text-[11px] text-muted-foreground">La stå tomt om personen ikke er registrert – kontaktpersonen bekrefter selv med BankID ved første innlogging.</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" data-testid="owner-contact-cancel" onClick={onClose}>Avbryt</Button>
          <Button className="gap-2" data-testid="owner-contact-save" onClick={save}>
            <Check className="h-4 w-4" /> Lagre kontaktperson
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
